import { useEffect, useState } from "preact/hooks";
import type { Offset } from "../engine/attributeOffsets";
import { browserDeliveryEnv, reminderText } from "../app/backup";
import type { DashboardViewModel, GrinderReferenceInfo, NudgeInfo, TuningInfo } from "../app/dashboardViewModel";
import { formatSeconds, parseNumber } from "../app/format";
import { defaultConfig, tunedParamsOf, type Recipe, type TunedParams } from "../engine/config";
import type { Grinder } from "../engine/model";
import type { ConfigVersion } from "../storage/repositories";
import { formatGrind, grindInputError, parseGrind } from "../app/grindFormat";
import { calibrationDate } from "./ShotHistory";
import { ConfirmDialog, Modal, type ConfirmState } from "./dialogs";

/**
 * Settings: the recipe; per grinder the reference setting, x_ref and seed / learned slope, with "Grinder
 * recalibrated" and its undo; taste nudges; the learned offsets table with a reset; and automatic tuning with its
 * history, "Revert to previous" and "Reset to defaults". Every learned value shows here and can be undone.
 */
const PARAM_TEXT: Record<keyof TunedParams, [string, (v: number) => string]> = {
  recencyHalfLifeD: ["recent-shot half-life", (v) => `${v.toFixed(1)} d`],
  agingRatePerDay: ["aging rate", (v) => `${(v * 100).toFixed(2)} %/day`],
  priorPseudoShots: ["grinder-slope weight", (v) => `${v.toFixed(1)} shots`],
  priorInterceptWeight: ["starting-point weight", (v) => v.toFixed(2)],
};

const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
const shortDate = (ts: string) => `${Number(ts.slice(8, 10))} ${MONTHS[Number(ts.slice(5, 7)) - 1]}`;

/** "recent-shot half-life 7.0 d → 9.3 d · aging rate 1.50 → 1.75 %/day" (unchanged parameters left out). */
export function paramChanges(before: TunedParams, after: TunedParams): string {
  const parts: string[] = [];
  for (const k of Object.keys(PARAM_TEXT) as (keyof TunedParams)[]) {
    if (Math.abs(before[k] - after[k]) < 1e-12) continue;
    const [label, fmt] = PARAM_TEXT[k];
    parts.push(`${label} ${fmt(before[k])} → ${fmt(after[k])}`);
  }
  return parts.length === 0 ? "no change" : parts.join(" · ");
}

function versionLine(v: ConfigVersion, all: ConfigVersion[]): string {
  const parent = all.find((x) => x.version === v.parentVersion)?.params ?? tunedParamsOf(defaultConfig());
  const what = v.kind === "tuned" ? "Tuned" : v.kind === "revert" ? "Reverted" : "Reset to defaults";
  const score = v.scoreBefore !== null && v.scoreAfter !== null
    ? ` · error score ${v.scoreBefore.toFixed(4)} → ${v.scoreAfter.toFixed(4)} (${Math.round((1 - v.scoreAfter / v.scoreBefore) * 100)} % better)` : "";
  return `${shortDate(v.createdAt)} · ${what}: ${paramChanges(parent, v.params)}${score}`;
}

function RecipeEditor(props: { vm: DashboardViewModel }) {
  const r = props.vm.active.value.recipe;
  const [vals, setVals] = useState({ doseG: String(r.doseG), yieldG: String(r.yieldG), timeLowS: String(r.timeLowS), timeHighS: String(r.timeHighS) });
  const [msg, setMsg] = useState("");
  const save = async () => {
    const n = (s: string) => parseNumber(s) ?? Number.NaN;
    const recipe: Recipe = { doseG: n(vals.doseG), yieldG: n(vals.yieldG), timeLowS: n(vals.timeLowS), timeHighS: n(vals.timeHighS) };
    try {
      await props.vm.saveRecipe(recipe);
      setMsg(`Saved: target ${formatSeconds((recipe.timeLowS + recipe.timeHighS) / 2)} s.`);
    } catch (e) {
      setMsg(e instanceof Error ? e.message : String(e));
    }
  };
  const field = (label: string, key: keyof typeof vals) => (
    <label class="field">{label}
      <input type="text" inputMode="decimal" value={vals[key]} onInput={(e) => setVals({ ...vals, [key]: (e.currentTarget as HTMLInputElement).value })} />
    </label>
  );
  return (
    <>
      <h3 class="section-title" style={{ fontSize: 15, marginTop: 8 }}>Recipe</h3>
      <div class="grid2">
        {field("Dose (g)", "doseG")}
        {field("Yield (g)", "yieldG")}
        {field("Time from (s)", "timeLowS")}
        {field("Time to (s)", "timeHighS")}
      </div>
      <div class="row">
        <span class="hint grow">Target is the middle of the window. Time is from pump start, including pre-infusion.</span>
        <button class="secondary" type="button" onClick={() => void save()}>Save recipe</button>
      </div>
      {msg && <div class="hint" role="status">{msg}</div>}
    </>
  );
}
const SOURCE_LABEL = { HISTORY: "from your history", GRINDER_SETTING: "from the setting below", MIDPOINT: "dial midpoint" } as const;

export function SettingsDialog(props: {
  open: boolean; vm: DashboardViewModel; onClose: () => void;
  onOpenBeans: () => void; onOpenGrinders: () => void; onOpenBackup: () => void;
}) {
  const [refs, setRefs] = useState<GrinderReferenceInfo[]>([]);
  const [tuning, setTuning] = useState<TuningInfo | null>(null);
  const [nudged, setNudged] = useState<NudgeInfo[]>([]);
  const [offsets, setOffsets] = useState<Offset[]>([]);
  const [edits, setEdits] = useState<Record<number, string>>({});
  const [error, setError] = useState("");
  const [backupStatus, setBackupStatus] = useState("");
  const backupNow = async () => {
    const r = await props.vm.exportBackup(browserDeliveryEnv());
    setBackupStatus(r === "cancelled" ? "Backup not saved." : "Backup saved. Thanks!");
  };
  const [confirm, setConfirm] = useState<ConfirmState | null>(null);

  const reload = async () => {
    setRefs(await props.vm.grinderReferences());
    setOffsets(await props.vm.learnedOffsets());
    setTuning(await props.vm.tuningInfo());
    setNudged(await props.vm.nudgedBeans());
    setEdits({});
    setError("");
  };
  useEffect(() => { if (props.open) void reload(); }, [props.open]);

  const saveRef = async (info: GrinderReferenceInfo) => {
    const g = info.grinder;
    const text = edits[g.id as number];
    if (text === undefined) return;
    const value = text.trim() === "" ? null : parseGrind(text, g);
    if (text.trim() !== "" && value === null) { setError(grindInputError(g).replace("Grind setting", "Reference setting")); return; }
    try {
      await props.vm.saveGrinder({ ...g, referenceSetting: value });
      await reload();
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    }
  };

  const askRecalibrate = (g: Grinder) => setConfirm({
    title: "Grinder recalibrated",
    message: `Start fresh on the ${g.name}: shots pulled before now stop counting for predictions, the starting point and learning. They stay in your history, dimmed, and can be brought back with Undo. Learned bean-type offsets are kept.`,
    confirmLabel: "Recalibrated",
    onConfirm: () => void props.vm.recalibrateGrinder(g).then(reload),
  });

  const askUndo = (g: Grinder) => setConfirm({
    title: "Undo recalibration",
    message: `Count every earlier shot on the ${g.name} again, as before the recalibration on ${calibrationDate(g.calibratedSince ?? "")}.`,
    confirmLabel: "Undo",
    onConfirm: () => void props.vm.undoRecalibration(g).then(reload),
  });

  const askRevert = () => setConfirm({
    title: "Revert to previous",
    message: "Go back to the predictor values in force before the latest change. The change stays in the history below.",
    confirmLabel: "Revert",
    onConfirm: () => void props.vm.revertTuning().then(reload),
  });

  const askResetTuning = () => setConfirm({
    title: "Reset to defaults",
    message: "Put the four tuned predictor values back to the shipped defaults. Automatic tuning carries on from there as more shots come in.",
    confirmLabel: "Reset",
    danger: true,
    onConfirm: () => void props.vm.resetTuning().then(reload),
  });

  const askReset = () => setConfirm({
    title: "Reset learned offsets",
    message: "Put every attribute offset back to its seed value and forget which beans have been learned from. Learning re-runs from your dialed-in beans straight away.",
    confirmLabel: "Reset",
    danger: true,
    onConfirm: () => void props.vm.resetOffsetsToSeeds().then(reload),
  });

  const grouped = new Map<string, Offset[]>();
  for (const o of offsets) grouped.set(o.attribute, [...(grouped.get(o.attribute) ?? []), o]);

  return (
    <>
      <Modal open={props.open} onClose={props.onClose}>
        <h2>Settings</h2>

        {props.vm.backup.value.due && (
          <button class="block" type="button" onClick={() => void backupNow()}>{reminderText(props.vm.backup.value)}</button>
        )}
        {backupStatus && <div class="hint" role="status">{backupStatus}</div>}

        <div class="sheet">
          <button class="secondary block" type="button" onClick={() => { props.onClose(); props.onOpenBeans(); }}>Beans</button>
          <button class="secondary block" type="button" onClick={() => { props.onClose(); props.onOpenGrinders(); }}>Grinders</button>
          <button class="secondary block" type="button" onClick={() => { props.onClose(); props.onOpenBackup(); }}>Backup &amp; restore</button>
        </div>
        {props.open && <RecipeEditor vm={props.vm} />}

        <h3 class="section-title" style={{ fontSize: 15, marginTop: 8 }}>Starting point per grinder</h3>
        <div class="hint">x_ref is where a fresh medium, washed, standard, caffeinated bean dials in. It comes from your dialed-in beans when there are any; otherwise from the reference setting you enter here; otherwise the dial midpoint.</div>
        {refs.map((info) => {
          const g = info.grinder;
          const id = g.id as number;
          const current = edits[id] ?? (g.referenceSetting === null ? "" : formatGrind(g.referenceSetting, g));
          return (
            <div key={id} class="plain-list" style={{ marginTop: 6 }}>
              <div style={{ fontSize: 14, fontWeight: 600 }}>{g.name}</div>
              <div class="hint">
                x_ref {formatGrind(info.reference.xRef, g)} · {SOURCE_LABEL[info.reference.source]}
                {info.reference.source === "HISTORY" ? ` (${info.reference.contributingBeans} ${info.reference.contributingBeans === 1 ? "bean" : "beans"})` : ""}
              </div>
              <div class="hint" data-testid="slope-line">
                {info.slope.source === "learned"
                  ? `Slope: seed ${info.slope.seed} · learned ${info.slope.slope.toFixed(3)} from ${info.slope.contributingBeans} ${info.slope.contributingBeans === 1 ? "bean" : "beans"}`
                  : `Slope: seed ${info.slope.seed} · not learned yet (needs a bean with 4 shots across 3 dial steps)`}
              </div>
              <div class="row">
                <input class="grow" type="text" inputMode={g.dialFormat === "macro_micro" ? "text" : "decimal"} aria-label={`Reference setting for ${g.name}`}
                       placeholder={`Reference setting (${formatGrind(g.minGrind, g)}–${formatGrind(g.maxGrind, g)}), blank = midpoint`}
                       value={current} onInput={(e) => setEdits({ ...edits, [id]: (e.currentTarget as HTMLInputElement).value })} />
                <button class="secondary" type="button" disabled={edits[id] === undefined} onClick={() => void saveRef(info)}>Save</button>
              </div>
              {g.calibratedSince ? (
                <div class="row">
                  <span class="hint grow">Recalibrated {calibrationDate(g.calibratedSince)}; earlier shots are not used.</span>
                  <button class="secondary" type="button" onClick={() => askUndo(g)}>Undo recalibration</button>
                </div>
              ) : (
                <button class="ghost block" type="button" onClick={() => askRecalibrate(g)}>Grinder recalibrated (e.g. shim added)</button>
              )}
            </div>
          );
        })}

        <h3 class="section-title" style={{ fontSize: 15, marginTop: 10 }}>Learned bean-type offsets</h3>
        <div class="hint">ln-time offsets relative to the reference bean; negative runs faster (starts finer). n = beans learned from.</div>
        <table class="offsets">
          <tbody>
            {[...grouped.entries()].map(([attribute, rows]) => (
              <>
                <tr key={attribute} class="group"><td colSpan={3}>{attribute.replace("_", " ")}</td></tr>
                {rows.map((o) => (
                  <tr key={`${o.attribute}:${o.value}`}>
                    <td>{o.value.replace("_", " ")}</td>
                    <td class="num">{o.deltaLn >= 0 ? "+" : ""}{o.deltaLn.toFixed(3)}</td>
                    <td class="num hint">n={o.nBeans}</td>
                  </tr>
                ))}
              </>
            ))}
          </tbody>
        </table>
        <button class="secondary block" type="button" onClick={askReset}>Reset offsets to seeds</button>
        {props.vm.lastLearned.value.length > 0 && (
          <div class="hint">Learned from {props.vm.lastLearned.value.length} bean{props.vm.lastLearned.value.length === 1 ? "" : "s"} at last launch.</div>
        )}

        <h3 class="section-title" style={{ fontSize: 15, marginTop: 10 }}>Taste feedback</h3>
        {nudged.length === 0 ? (
          <div class="hint">No bean targets have been nudged. Three taste-tagged shots in the window that lean sour (or bitter) move that bean's target by 1 s.</div>
        ) : (
          <ul class="plain-list">
            {nudged.map((n) => (
              <li key={n.bean.id} class="hint">
                {n.bean.name}: target {n.offsetS >= 0 ? "+" : ""}{formatSeconds(n.offsetS)} s{n.paused ? " · paused (tastes disagreed)" : ""}. Undo from the bean menu.
              </li>
            ))}
          </ul>
        )}

        <h3 class="section-title" style={{ fontSize: 15, marginTop: 10 }}>Automatic tuning</h3>
        {tuning && (
          <>
            <div class="hint">
              Predictor version <code>{tuning.activeVersion}</code> is stamped on every shot you save. Tuning replays your history on launch once you have 40 valid shots, then every 20 more, and adopts a change only when it predicts clearly better.
              {" "}{tuning.status.shotsUntilNext > 0 ? `Next check after ${tuning.status.shotsUntilNext} more valid ${tuning.status.shotsUntilNext === 1 ? "shot" : "shots"}.` : "Next check on the next launch."}
            </div>
            {tuning.versions.length > 0 && (
              <ul class="plain-list" data-testid="tuning-history">
                {tuning.versions.map((v) => <li key={v.version} class="hint">{versionLine(v, tuning.versions)}</li>)}
              </ul>
            )}
            <div class="grid2">
              <button class="secondary" type="button" disabled={!tuning.canRevert} onClick={askRevert}>Revert to previous</button>
              <button class="secondary" type="button" disabled={!tuning.canRevert} onClick={askResetTuning}>Reset to defaults</button>
            </div>
          </>
        )}

        {error && <div class="error">{error}</div>}
        <div class="actions"><button class="secondary" type="button" onClick={props.onClose}>Close</button></div>
      </Modal>
      <ConfirmDialog state={confirm} onClose={() => setConfirm(null)} />
    </>
  );
}
